/* =============================================================================
   Monolith — product-card.js
   Behaviour for snippets/product-card.liquid:
   - image carousel (dots, prev/next, keyboard)
   - quick add (size buttons → Ajax cart) with inline feedback
   - mobile "+" toggle for the quick add panel
   Uses event delegation so it also works on content injected via the
   Section Rendering API (filters, recommendations, search).
   ============================================================================ */
(function () {
  'use strict';

  const SELECTORS = {
    card: '[data-product-card]',
    carousel: '[data-card-carousel]',
    slide: '.product-card__slide',
    dot: '[data-card-dot]',
    prev: '[data-card-prev]',
    next: '[data-card-next]',
    quickAdd: '[data-quick-add]',
    quickAddVariant: '[data-quick-add-variant]',
    quickAddFeedback: '[data-quick-add-feedback]',
    mobileToggle: '[data-quick-add-mobile-toggle]'
  };

  /* ------------------------------------------------------------ Carousel */
  function setSlide(carousel, index) {
    const slides = carousel.querySelectorAll(SELECTORS.slide);
    const dots = carousel.querySelectorAll(SELECTORS.dot);
    if (!slides.length) return;
    const total = slides.length;
    const target = ((index % total) + total) % total;

    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === target));
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === target));
    carousel.dataset.activeIndex = String(target);

    // Disable the CSS hover swap once the user has interacted
    const card = carousel.closest(SELECTORS.card);
    if (card) card.classList.add('product-card--interacted');

    // Ensure the target image is loaded (it may still be lazy)
    const img = slides[target].querySelector('img[loading="lazy"]');
    if (img) img.loading = 'eager';
  }

  function currentIndex(carousel) {
    return parseInt(carousel.dataset.activeIndex || '0', 10);
  }

  document.addEventListener('click', (event) => {
    const dot = event.target.closest(SELECTORS.dot);
    if (dot) {
      event.preventDefault();
      const carousel = dot.closest(SELECTORS.carousel);
      if (carousel) setSlide(carousel, parseInt(dot.dataset.cardDot, 10));
      return;
    }
    const prev = event.target.closest(SELECTORS.prev);
    if (prev) {
      event.preventDefault();
      const carousel = prev.closest(SELECTORS.carousel);
      if (carousel) setSlide(carousel, currentIndex(carousel) - 1);
      return;
    }
    const next = event.target.closest(SELECTORS.next);
    if (next) {
      event.preventDefault();
      const carousel = next.closest(SELECTORS.carousel);
      if (carousel) setSlide(carousel, currentIndex(carousel) + 1);
    }
  });

  // Touch swipe on card media
  let touchStartX = null;
  let touchCarousel = null;
  document.addEventListener(
    'touchstart',
    (event) => {
      const carousel = event.target.closest(SELECTORS.carousel);
      if (!carousel) return;
      touchCarousel = carousel;
      touchStartX = event.touches[0].clientX;
    },
    { passive: true }
  );
  document.addEventListener(
    'touchend',
    (event) => {
      if (touchStartX === null || !touchCarousel) return;
      const delta = event.changedTouches[0].clientX - touchStartX;
      if (Math.abs(delta) > 40) {
        setSlide(touchCarousel, currentIndex(touchCarousel) + (delta < 0 ? 1 : -1));
      }
      touchStartX = null;
      touchCarousel = null;
    },
    { passive: true }
  );

  /* ------------------------------------------------------------ Quick add */
  function setFeedback(quickAdd, state, message) {
    quickAdd.dataset.quickAddState = state;
    quickAdd.setAttribute('aria-busy', state === 'adding' ? 'true' : 'false');
    const feedback = quickAdd.querySelector(SELECTORS.quickAddFeedback);
    if (feedback) feedback.textContent = message || '';
  }

  async function quickAddVariant(button) {
    const quickAdd = button.closest(SELECTORS.quickAdd);
    const variantId = button.dataset.quickAddVariant;
    if (!quickAdd || !variantId || quickAdd.dataset.quickAddState === 'adding') return;

    button.dataset.loading = 'true';
    setFeedback(quickAdd, 'adding', theme.strings.adding);

    try {
      await theme.cart.add([{ id: Number(variantId), quantity: 1 }], { openDrawer: true });
      setFeedback(quickAdd, 'success', theme.strings.added);
    } catch (error) {
      setFeedback(quickAdd, 'error', error.message || theme.strings.error);
    } finally {
      delete button.dataset.loading;
      setTimeout(() => {
        setFeedback(quickAdd, 'idle', '');
        quickAdd.classList.remove('is-mobile-open');
      }, 1400);
    }
  }

  document.addEventListener('click', (event) => {
    const variantBtn = event.target.closest(SELECTORS.quickAddVariant);
    if (variantBtn && !variantBtn.disabled) {
      event.preventDefault();
      quickAddVariant(variantBtn);
      return;
    }

    const mobileToggle = event.target.closest(SELECTORS.mobileToggle);
    if (mobileToggle) {
      event.preventDefault();
      const shell = mobileToggle.closest('.product-card__media-shell');
      const quickAdd = shell && shell.querySelector(SELECTORS.quickAdd);
      if (!quickAdd) return;
      // Close other open panels
      document.querySelectorAll(`${SELECTORS.quickAdd}.is-mobile-open`).forEach((el) => {
        if (el !== quickAdd) el.classList.remove('is-mobile-open');
      });
      const isOpen = quickAdd.classList.toggle('is-mobile-open');
      mobileToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      return;
    }

    // Tap outside closes mobile quick-add panels
    if (!event.target.closest(SELECTORS.quickAdd)) {
      document.querySelectorAll(`${SELECTORS.quickAdd}.is-mobile-open`).forEach((el) => el.classList.remove('is-mobile-open'));
    }
  });

  // Single-variant quick add forms are handled by cart.js (data-product-form submit),
  // we only add visual feedback here.
  theme.events.on('cart:form-submit', ({ form }) => {
    const quickAdd = form.closest(SELECTORS.quickAdd);
    if (quickAdd) setFeedback(quickAdd, 'adding', theme.strings.adding);
  });
  theme.events.on('cart:form-success', ({ form }) => {
    const quickAdd = form.closest(SELECTORS.quickAdd);
    if (!quickAdd) return;
    setFeedback(quickAdd, 'success', theme.strings.added);
    setTimeout(() => setFeedback(quickAdd, 'idle', ''), 1400);
  });
  theme.events.on('cart:form-error', ({ form, message }) => {
    const quickAdd = form.closest(SELECTORS.quickAdd);
    if (!quickAdd) return;
    setFeedback(quickAdd, 'error', message);
    setTimeout(() => setFeedback(quickAdd, 'idle', ''), 2000);
  });
})();
